import React, { useState, useEffect, useRef } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { isMobile } from 'react-device-detect'
import classNames from 'classnames'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

interface ResponsiveSize {
  width: number
  height: number
  navButtonWidth: number
  navButtonHeight: number
}

interface ImageSlideModalProps {
  isOpen: boolean
  onClose: () => void
  images: string[]
  initialIndex?: number
  showCounter?: boolean
}

export const ImageSlideModal: React.FC<ImageSlideModalProps> = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  showCounter = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [responsiveSize, setResponsiveSize] = useState<ResponsiveSize>({
    width: 320,
    height: 680,
    navButtonWidth: 22,
    navButtonHeight: 86,
  })
  const [imageAspectRatio, setImageAspectRatio] = useState<number>(9 / 16)
  const sliderRef = useRef<Slider>(null)

  // 이미지 비율 감지 함수
  const detectImageAspectRatio = (imageUrl: string) => {
    const img = new Image()
    img.onload = () => {
      const ratio = img.width / img.height
      setImageAspectRatio(ratio)
    }
    img.src = imageUrl
  }

  // 반응형 크기 계산 함수
  const calculateResponsiveSize = (): ResponsiveSize => {
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight

    // 현재 이미지의 실제 비율 사용
    const designRatio = imageAspectRatio

    // 최소/최대 크기 제한
    const minWidth = isMobile ? 280 : 320
    const maxWidth = isMobile ? screenWidth * 0.9 : 900
    const maxHeight = isMobile ? screenHeight * 0.8 : 800

    // 화면 크기에 따른 최적 크기 계산
    const availableWidth = Math.min(screenWidth * 0.9, maxWidth)
    const availableHeight = Math.min(screenHeight * 0.85, maxHeight)

    // 비율 유지하면서 크기 조정
    let finalWidth = Math.max(availableWidth, minWidth)
    let finalHeight = finalWidth / designRatio

    if (finalHeight > availableHeight) {
      finalHeight = availableHeight
      finalWidth = finalHeight * designRatio
      finalWidth = Math.max(finalWidth, minWidth)
    }

    // 네비게이션 버튼 크기 계산
    const sizeRatio = finalWidth / 600
    const navButtonWidth = Math.max(
      isMobile ? 20 : 22,
      Math.round(22 * sizeRatio)
    )
    const navButtonHeight = Math.max(
      isMobile ? 70 : 86,
      Math.round(86 * sizeRatio)
    )

    return {
      width: Math.round(finalWidth),
      height: Math.round(finalHeight),
      navButtonWidth,
      navButtonHeight,
    }
  }

  // 이미지 변경 시 비율 감지
  useEffect(() => {
    if (isOpen && images[currentIndex]) {
      detectImageAspectRatio(images[currentIndex])
    }
  }, [isOpen, currentIndex, images])

  // 비율 변경 시 크기 재계산
  useEffect(() => {
    if (isOpen) {
      setResponsiveSize(calculateResponsiveSize())
    }
  }, [imageAspectRatio, isOpen])

  // 윈도우 리사이즈 이벤트 핸들링
  useEffect(() => {
    const handleResize = () => {
      setResponsiveSize(calculateResponsiveSize())
    }

    handleResize()

    if (isOpen) {
      window.addEventListener('resize', handleResize)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isOpen, imageAspectRatio])

  // 키보드 네비게이션
  useEffect(() => {
    const handleKeyNavigation = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose()
          break
        case 'ArrowLeft':
          event.preventDefault()
          handlePrevImage()
          break
        case 'ArrowRight':
          event.preventDefault()
          handleNextImage()
          break
        default:
          break
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyNavigation)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyNavigation)
    }
  }, [isOpen, currentIndex, images.length])

  // initialIndex 변경 시 currentIndex 업데이트
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(Math.max(0, Math.min(initialIndex, images.length - 1)))
    }
  }, [isOpen, initialIndex, images.length])

  // 데스크톱에서 인덱스 변경 시 모바일 슬라이더 동기화
  useEffect(() => {
    if (isMobile && sliderRef.current) {
      sliderRef.current.slickGoTo(currentIndex)
    }
  }, [currentIndex])

  if (!isOpen || images.length === 0) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handlePrevImage = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNextImage = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  // 모바일 슬라이더 설정
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchMove: true,
    arrows: false,
    initialSlide: currentIndex,
    afterChange: (current: number) => {
      setCurrentIndex(current)
    },
    swipe: true,
    draggable: true,
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[70]"
      onClick={handleBackdropClick}
    >
      {isMobile ? (
        /* 모바일 레이아웃: react-slick 슬라이더 */
        <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden">
          {/* 모바일 닫기 버튼 */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
          >
            <X size={24} className="text-white" />
          </button>

          {/* react-slick 슬라이더 */}
          <div
            className="relative"
            style={{
              width: `${responsiveSize.width}px`,
              height: `${responsiveSize.height}px`,
            }}
          >
            <Slider ref={sliderRef} {...sliderSettings}>
              {images.map((imageUrl, index) => (
                <div key={index} className="outline-none">
                  <img
                    src={imageUrl}
                    alt={`Image ${index + 1}`}
                    className="object-contain select-none rounded-[10px]"
                    style={{
                      width: `${responsiveSize.width}px`,
                      height: `${responsiveSize.height}px`,
                    }}
                    draggable={false}
                  />
                </div>
              ))}
            </Slider>
          </div>

          {/* 모바일 카운터 */}
          {showCounter && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 rounded-full px-4 py-2">
              <span className="text-white text-sm">
                {currentIndex + 1} / {images.length}
              </span>
            </div>
          )}
        </div>
      ) : (
        /* 데스크톱 레이아웃 */
        <div className="relative flex items-center justify-center gap-8">
          {/* 왼쪽 네비게이션 버튼 */}
          <button
            onClick={handlePrevImage}
            disabled={currentIndex === 0}
            className={classNames(
              'bg-black bg-opacity-50 rounded-md px-1 py-4 transition-colors flex items-center justify-center',
              {
                'hover:bg-opacity-70': currentIndex > 0,
                'opacity-50 cursor-not-allowed': currentIndex === 0,
              }
            )}
            style={{
              width: `${responsiveSize.navButtonWidth}px`,
              height: `${responsiveSize.navButtonHeight}px`,
            }}
          >
            <ChevronLeft size={16} className="text-white" />
          </button>

          {/* 중앙 이미지 */}
          <div className="relative flex flex-col items-center gap-4">
            <img
              src={images[currentIndex]}
              alt={`Image ${currentIndex + 1}`}
              className="object-contain rounded-[10px]"
              style={{
                width: `${responsiveSize.width}px`,
                height: `${responsiveSize.height}px`,
              }}
            />

            {/* 데스크톱 카운터 */}
            {showCounter && (
              <div className="bg-black bg-opacity-50 rounded-full px-4 py-2">
                <span className="text-white text-sm">
                  {currentIndex + 1} / {images.length}
                </span>
              </div>
            )}
          </div>

          {/* 오른쪽 네비게이션 버튼 */}
          <button
            onClick={handleNextImage}
            disabled={currentIndex === images.length - 1}
            className={classNames(
              'bg-black bg-opacity-50 rounded-md px-1 py-4 transition-colors flex items-center justify-center',
              {
                'hover:bg-opacity-70': currentIndex < images.length - 1,
                'opacity-50 cursor-not-allowed':
                  currentIndex === images.length - 1,
              }
            )}
            style={{
              width: `${responsiveSize.navButtonWidth}px`,
              height: `${responsiveSize.navButtonHeight}px`,
            }}
          >
            <ChevronRight size={16} className="text-white" />
          </button>

          {/* 데스크톱 닫기 버튼 */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
        </div>
      )}
    </div>
  )
}
